import { showNotification } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { v4 as uuidv4 } from 'uuid';

import { FEATURE } from '~/common/enums/feature.enum';
import { PaymentType } from '~/common/enums/payments.enum';
import { reportEvent } from '~/domains/analytics';
import { MessageKeys } from '~/domains/payments/deposits/components/consts';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { createSkrillHandle } from '~/services/wallet/wallet.service.api';
import { getErrorTranslations } from '~/domains/payments/utils/utils';

import { isCustomDeposit } from './consts';
import { getFallbackUrl } from './getFallbackUrl';

function useDepositWithSkrill() {
  const { t } = useTranslation('payments');
  const useSkrillInternalSimulator = useIsFeatureEnabled(FEATURE.USE_SKRILL_INTERNAL_SIMULATOR);

  return useMutation({
    mutationFn: async ({
      depositAmountWithFees,
      merchantRefNum = uuidv4(),
      contestIdForPostDeposit,
      fallbackUrl,
    }: {
      depositAmount: number;
      depositAmountWithFees: number;
      merchantRefNum?: string;
      contestIdForPostDeposit?: string;
      fallbackUrl: string;
      bonusId?: string;
      redirectTo?: string;
    }) =>
      createSkrillHandle({
        amount: depositAmountWithFees,
        currency: 'USD',
        merchantRefNum,
        simulator: useSkrillInternalSimulator ? 'INTERNAL' : 'EXTERNAL',
        transactionType: 'PAYMENT',
        returnLinks: [
          {
            rel: 'default',
            href: getFallbackUrl(fallbackUrl),
            method: 'GET',
          },
          {
            rel: 'on_completed',
            href: `${window.location.origin}/account/wallet/deposit/success?type=${
              PaymentType.SKRILL
            }${contestIdForPostDeposit ? `&contestId=${contestIdForPostDeposit}` : ''}`,
            method: 'POST',
          },
          {
            rel: 'on_failed',
            href: `${window.location.origin}/account/wallet/deposit?message=${MessageKeys.REJECTED}&type=Skrill`,
            method: 'POST',
          },
        ],
      }),
    onSuccess: (response, payload) => {
      if (response.data.links) {
        reportEvent('Transactions > Deposit', {
          deposit_id: response.id,
          deposit_method: 'Skrill',
          deposit_amount: payload.depositAmount,
          is_custom_deposit: isCustomDeposit(payload.depositAmount),
          currency: 'USD',
          bonus_id: payload.bonusId,
        });

        localStorage.setItem(
          'depositData',
          JSON.stringify({
            paymentHandleId: response.data.id,
            paymentHandleToken: response.data.paymentHandleToken,
            paymentType: PaymentType.SKRILL,
            amount: payload.depositAmountWithFees,
            displayAmount: payload.depositAmount,
            merchantRefNum: payload.merchantRefNum,
            bonusId: payload.bonusId,
            redirectTo: payload.redirectTo,
          })
        );
        const redirectLink = response.data.links[0].href;
        window.location.href = redirectLink;
      } else {
        showNotification({
          // @ts-expect-error Missing types for error response in Swagger
          title: response.error,
          // @ts-expect-error Missing types for error response in Swagger
          message: response?.data?.message || response.message,
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage = getErrorTranslations(t, error.code);

      if (error.code === 'ERR_BAD_REQUEST') {
        errorMessage.title = t('deposits.errors.skrill');
        errorMessage.message = error.response?.data?.message ?? '';
      }

      showNotification({
        title: errorMessage.title,
        message: errorMessage.message,
        color: 'red',
        autoClose: 5000,
      });
    },
  });
}

export { useDepositWithSkrill };
